<template>
    <Teleport to="body">
        <div class="product-listing-select">
            <div
                class="fixed inset-0 bg-black opacity-40 z-50"
                @click="closeSelection(true)"
            />
            <div
                :id="dialogId"
                ref="panel"
                role="dialog"
                aria-modal="true"
                :aria-labelledby="`${dialogId}-title`"
                :aria-describedby="`${dialogId}-product`"
                :aria-busy="variantLoading"
                tabindex="-1"
                class="fixed flex flex-col overflow-hidden p-3 pt-0 bg-white z-50 h-fit rounded-lg border-border border shadow-xl focus:outline-none"
                :style="panelStyle"
                @keydown="onKeydown"
            >
                <div class="flex shrink-0 w-full justify-end text-black pb-3">
                    <button
                        type="button"
                        class="h-11 w-16 flex justify-end items-center focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-current"
                        :aria-label="t('product.card.select.close')"
                        @click="closeSelection(true)"
                    >
                        <MiscIcon
                            :name="'x'"
                            :size="'sm'"
                        />
                    </button>
                </div>
                <div :id="`${dialogId}-title`" class="w-full flex shrink-0 justify-center font-semibold text-lg text-link pb-6">
                    {{ t('product.card.select.chooseQuantity') }}
                </div>
                <span :id="`${dialogId}-product`" class="sr-only">{{ product.name }}</span>

                <!-- The scroll container is a div: a fieldset does not clip its overflow reliably as a flex item. -->
                <div v-if="orderableVariants.length > 0" class="mb-6 min-h-0 overflow-y-auto">
                    <fieldset class="min-w-0">
                        <legend class="sr-only">
                            {{ t('product.card.select.variantLegend') }}
                        </legend>
                        <div
                            v-for="variant in orderableVariants"
                            :key="variant.id"
                            class="max-h-full"
                        >
                            <label
                                class="cursor-pointer variant-container min-h-[74px] p-2 rounded block border has-[:focus-visible]:outline-2 has-[:focus-visible]:-outline-offset-2 has-[:focus-visible]:outline-link"
                                :class="selectedVariant == variant.id ? 'bg-base-200 border-link selected' : 'border-white hover:border-border'"
                            >
                                <div class="flex flex-row justify-between">
                                    <div class="max-w-24 w-full flex items-center">
                                        <div
                                            class="text-base leading-5 w-full"
                                            :class="{
                                                'font-bold': selectedVariant == variant.id
                                            }"
                                        >
                                            <div class="variant-name flex flex-row">
                                                {{ variant.options[0]?.translated?.name }}
                                                <div v-if="variant.translated?.customFields?.expiration_date != null">
                                                    &nbsp;- {{ t('product.card.select.expirationDate') }}
                                                </div>
                                            </div>
                                            <div v-if="variant.translated?.customFields?.expiration_date != null">
                                                {{ variant.translated?.customFields?.expiration_date }}
                                            </div>
                                        </div>
                                    </div>

                                    <div class="w-full flex items-center">
                                        <ProductListingSelectPrice
                                            class="w-full"
                                            :variant="variant"
                                        />
                                    </div>
                                </div>

                                <input
                                    v-model="selectedVariant"
                                    :value="variant.id"
                                    :name="`${dialogId}-variant`"
                                    type="radio"
                                    class="sr-only radio"
                                    @change="selectedVariantProduct = variant"
                                >
                            </label>
                        </div>
                    </fieldset>
                </div>

                <div
                    class="h-11 min-h-11 shrink-0 mb-2 flex items-center justify-center rounded-lg bg-primary"
                    :class="{
                        'opacity-50': variantLoading || allVariantsNotOrderable
                    }"
                >
                    <button
                        class="quantity h-full w-10 flex justify-center items-center cursor-pointer disabled:cursor-default"
                        type="button"
                        :aria-label="t('product.card.select.decrease')"
                        :disabled="variantLoading || allVariantsNotOrderable"
                        @click="decrement"
                    >
                        <MiscIcon
                            :name="'minus'"
                            :size="'sm'"
                        />
                    </button>

                    <label :for="`${dialogId}-quantity`" class="sr-only">{{ t('product.card.select.inputLabel') }}</label>

                    <input
                        :id="`${dialogId}-quantity`"
                        v-model="quantity"
                        type="number"
                        :max="product.max"
                        :min="product.min"
                        class="w-[118px] flex justify-center font-semibold text-lg bg-transparent text-center"
                        :disabled="variantLoading || allVariantsNotOrderable"
                        @input="preventZero"
                    >

                    <button
                        class="quantity h-full w-10 flex justify-center items-center cursor-pointer disabled:cursor-default"
                        type="button"
                        :aria-label="t('product.card.select.increase')"
                        :disabled="variantLoading || allVariantsNotOrderable"
                        @click="increment"
                    >
                        <MiscIcon
                            :name="'plus'"
                            :size="'sm'"
                        />
                    </button>
                </div>
                <button
                    type="button"
                    class="btn shrink-0 text-center font-semibold text-lg w-full h-12 border-none rounded-lg active bg-buy text-white hover:bg-buy-focus hover:text-buy-content focus-visible:outline-offset-[-4px] focus-visible:outline-current"
                    :disabled="loading || allVariantsNotOrderable"
                    :aria-busy="loading"
                    @click="onClickAddToCart()"
                >
                    <template v-if="loading">
                        <span class="loading" aria-hidden="true" />
                        <span class="sr-only">{{ t('product.card.select.loading') }}</span>
                    </template>
                    <div v-else class="flex gap-2 items-center">
                        {{ t('product.card.select.addToCart') }}
                        <MiscIcon
                            :name="'cart-box-arrow-down'"
                            :size="'lg'"
                        />
                    </div>
                </button>
            </div>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import type { HblProduct } from '@/utils/types'
import { useCart, useDrawer, useNuxtApp, useProductVariants } from '#imports'
import { useI18n } from 'vue-i18n'
import { computed, onBeforeUnmount, onMounted, ref, toRef } from 'vue'
import { useElementBounding, useWindowSize } from '@vueuse/core'

const { t } = useI18n()
const props = defineProps<{
    product: HblProduct,
    anchor: HTMLElement | null,
    dialogId: string,
}>()

const emit = defineEmits<{
    closeSelection: [restoreFocus: boolean]
}>()

function closeSelection(restoreFocus: boolean) {
    emit('closeSelection', restoreFocus)
}

// Rendered in <body> so the backdrop covers the whole page, also inside a (transformed) Swiper slide;
// the panel is placed over the product card it belongs to, kept inside the viewport for partly visible slides.
const viewportGutter = 16
const { left, bottom, width, height } = useElementBounding(toRef(props, 'anchor'))
const { width: viewportWidth } = useWindowSize({ includeScrollbar: false })
const panelStyle = computed(() => ({
    left: `${Math.min(Math.max(left.value, viewportGutter), viewportWidth.value - width.value - viewportGutter)}px`,
    top: `${bottom.value}px`,
    width: `${width.value}px`,
    maxHeight: `${height.value}px`,
    transform: 'translateY(-100%)',
}))

const panel = ref<HTMLElement | null>(null)
const focusableSelector = 'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'

function onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
        event.stopPropagation()
        closeSelection(true)
        return
    }

    if (event.key !== 'Tab' || panel.value == null) {
        return
    }

    const focusable = [...panel.value.querySelectorAll<HTMLElement>(focusableSelector)]
    if (focusable.length === 0) {
        event.preventDefault()
        return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.value)) {
        event.preventDefault()
        last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
    }
}

const { toggleDrawer } = useDrawer()

const quantity = ref<number | null>(1)
const minQuantity = ref<number | null>(props.product.min - 1)
const selectedVariant = ref()

// Use the variants composable
const {
    variants,
    orderableVariants,
    allVariantsNotOrderable,
    loading: variantLoading,
    fetchVariants
} = useProductVariants()

const cartStore = useCart()
const { addToCart } = cartStore
const { loading: cartLoading, error: cartError } = storeToRefs(cartStore)
const { showNotification } = useNotification()
const loading = computed(() => {
    return cartLoading.value || variantLoading.value
})

const preventZero = (event: Event) => {
    const target = event.target as HTMLInputElement
    const value = Number(target.value)
    if (value === minQuantity.value) {
        target.value = ''
        quantity.value = props?.product?.min ? props?.product?.min : 0
    }
}

const { $hblBus } = useNuxtApp()

async function onClickAddToCart () {

    let id = props.product.id

    if(selectedVariant?.value != null){
        id = selectedVariant?.value
    }

    await addToCart(quantity?.value, id)

    if (!cartError?.value) {
        $hblBus.$emit('addToCart', { qty: quantity?.value, product: props?.product })
    }

    // Close first: the drawer focuses itself on nextTick, which must run after this dialog lifted `inert`.
    closeSelection(false)
    toggleDrawer('cart', 'right')
}

watch(cartError, (value) => {
    // useCart resets the error to false before every request; only real errors are shown.
    if (value) {
        showNotification(value, 'error', true)
    }
})

function decrement () {
    if (quantity.value > props?.product?.min) {
        quantity.value--
    }
}

function increment () {
    const upperLimit = props.product.max || props.product.stock
    if ((quantity.value ?? 0) < upperLimit) {
        quantity.value = (quantity.value ?? 0) + 1
    }
}

// The page behind the modal dialog is not reachable for screen readers in browse mode either.
const pageRoot = () => document.getElementById('__nuxt')

onBeforeUnmount(() => {
    pageRoot()?.removeAttribute('inert')
})

onMounted(async () => {
    pageRoot()?.setAttribute('inert', '')
    panel.value?.focus()

    await fetchVariants(props?.product?.id)

    if (variants.value.length > 0) {
        selectedVariant.value = variants.value[0].id
    }
})

</script>
